import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Wordmark from "@/components/Wordmark";
import { BUSINESS, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("You're on the list. Welcome to the Lounge.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#050505] text-white grain overflow-hidden">
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <Wordmark dark />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
              A calm, elegant space in Barrhaven where beauty and self-care come together.
              Facials, laser, waxing & advanced skincare — tailored to you.
            </p>

            <form
              onSubmit={subscribe}
              className="mt-8 max-w-sm"
              data-testid="newsletter-form"
              name="newsletter"
              id="newsletter-form"
            >
              <label
                htmlFor="newsletter-email"
                className="mb-3 block text-[11px] uppercase tracking-[0.2em] text-white/50"
              >
                Join the list — offers & beauty tips
              </label>
              <div className="flex border-b border-white/25">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  data-testid="newsletter-email"
                  name="email"
                  id="newsletter-email"
                  autoComplete="email"
                  className="w-full bg-transparent py-3 text-sm text-white placeholder:text-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="shrink-0 pl-4 text-white/70 transition-colors hover:text-white disabled:opacity-50"
                >
                  <ArrowUpRight size={20} />
                </button>
              </div>
            </form>
          </div>

          {/* Links */}
          <div className="lg:col-span-3">
            <h4 className="mb-5 text-[11px] uppercase tracking-[0.2em] text-white/50">Explore</h4>
            <ul className="space-y-3 text-sm text-white/75">
              {[
                { label: "Our Story", to: "/our-story" },
                { label: "Our Team", to: "/team" },
                { label: "Offers", to: "/offers" },
                { label: "Shop", to: "/shop" },
                { label: "FAQ", to: "/faq" },
                { label: "Contact", to: "/contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline transition-colors hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="mb-5 text-[11px] uppercase tracking-[0.2em] text-white/50">Services</h4>
            <ul className="space-y-3 text-sm text-white/75">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="link-underline transition-colors hover:text-white"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <h4 className="mb-5 text-[11px] uppercase tracking-[0.2em] text-white/50">Visit</h4>
            <ul className="space-y-4 text-sm text-white/75">
              <li className="flex gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-white/50" />
                <span>{BUSINESS.addressFull}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="mt-0.5 shrink-0 text-white/50" />
                <a href={BUSINESS.phoneHref} className="hover:text-white">
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li className="flex gap-3">
                <Clock size={16} className="mt-0.5 shrink-0 text-white/50" />
                <div className="space-y-2">
                  {BUSINESS.hours.map((h) => (
                    <div key={h.day} className="flex flex-col leading-snug">
                      <span className="text-white/85">{h.day}</span>
                      <span className="text-white/55">{h.time}</span>
                    </div>
                  ))}
                </div>
              </li>
              <li className="flex gap-3">
                <Instagram size={16} className="mt-0.5 shrink-0 text-white/50" />
                <a href={BUSINESS.instagram} target="_blank" rel="noreferrer" className="hover:text-white">
                  @barrhavenbeautylounge
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row md:items-center md:justify-between">
          <span>© {new Date().getFullYear()} Barrhaven Beauty Lounge. All rights reserved.</span>
          <span>Enhancing your natural beauty — with care & precision.</span>
        </div>
      </div>
    </footer>
  );
}
